// CookieConsent shared utilities and configuration
// This file contains all shared functionality that works across all domains

// Load cookie consent translations
let cookieTranslations = null;

async function loadCookieTranslations() {
    if (cookieTranslations) return cookieTranslations;

    try {
        // Load all cookie translation files and combine them
        const languages = ['en', 'es', 'de'];
        const translations = {};

        for (const lang of languages) {
            try {
                const response = await fetch(`/translations/cookies-${lang}.json`);
                if (response.ok) {
                    translations[lang] = await response.json();
                }
            } catch (err) {
                console.warn(`Failed to load cookie translations for ${lang}:`, err);
            }
        }

        cookieTranslations = translations;
        return cookieTranslations;
    } catch (error) {
        console.error('Failed to load cookie translations:', error);
        // Fallback to default translations
        console.warn('Using fallback cookie translations');
        return {
            en: {
                consentModal: {
                    title: 'We use cookies!',
                    description: 'Hi! This website uses cookies to ensure its proper operation and to track usage for analytics. You can choose to enable or disable analytics cookies.',
                    acceptAllBtn: 'Accept all',
                    acceptNecessaryBtn: 'Reject all',
                    showPreferencesBtn: 'Manage preferences'
                },
                preferencesModal: {
                    title: 'Cookie preferences',
                    acceptAllBtn: 'Accept all',
                    acceptNecessaryBtn: 'Reject all',
                    savePreferencesBtn: 'Save preferences',
                    closeIconLabel: 'Close modal',
                    policyLink: '',
                    sections: []
                }
            }
        };
    }
}

// Enhance preferences modal rendering to show cookie details and policy link
function enhancePreferencesModal(translations, lang) {
    if (!translations || !translations[lang] || !translations[lang].preferencesModal) return;
    const modal = document.querySelector('.cc_preferences_modal');
    if (!modal) return;

    // Render cookie details for each section
    const sections = translations[lang].preferencesModal.sections || [];
    const sectionEls = modal.querySelectorAll('.cc_section');
    sectionEls.forEach((sectionEl, idx) => {
        const section = sections[idx];
        if (!section || !section.cookies) return;
        // Remove old cookie table if exists
        const oldTable = sectionEl.querySelector('.cc_cookies_table');
        if (oldTable) oldTable.remove();
        // Create table
        const table = document.createElement('table');
        table.className = 'cc_cookies_table';
        table.innerHTML = `<thead><tr><th>Name</th><th>Purpose</th><th>Expiry</th><th>Provider</th></tr></thead><tbody></tbody>`;
        section.cookies.forEach(cookie => {
            const tr = document.createElement('tr');
            tr.innerHTML = `<td>${cookie.name}</td><td>${cookie.purpose}</td><td>${cookie.expiry}</td><td>${cookie.provider}</td>`;
            table.querySelector('tbody').appendChild(tr);
        });
        sectionEl.appendChild(table);
    });

    // Render policy link at the bottom
    const policyLink = translations[lang].preferencesModal.policyLink;
    if (policyLink) {
        let policyDiv = modal.querySelector('.cc_policy_link');
        if (!policyDiv) {
            policyDiv = document.createElement('div');
            policyDiv.className = 'cc_policy_link';
            policyDiv.style = 'margin: 24px 0 0 0; text-align: center; font-size: 14px;';
            modal.appendChild(policyDiv);
        }
        policyDiv.innerHTML = policyLink;
    }
}

// Shared CookieConsent configuration (domain-agnostic)
const getCookieConsentConfig = async (domainServices = {}, lang = 'en') => {
    const translations = await loadCookieTranslations();

    // Patch the preferences modal after it opens
    setTimeout(() => {
        // Listen for modal open
        document.body.addEventListener('click', async (e) => {
            if (e.target && e.target.closest('.cc_show_preferences_btn')) {
                // Wait for modal to render
                setTimeout(async () => {
                    const detectedLang = (window.CookieConsent && window.CookieConsent.language) || lang;
                    enhancePreferencesModal(translations, detectedLang);
                }, 100);
            }
        });
    }, 0);

    return {
        guiOptions: {
            consentModal: {
                layout: 'box',
                position: 'bottom right',
                transition: 'slide'
            },
            preferencesModal: {
                layout: 'box',
                position: 'right',
                transition: 'slide'
            }
        },
        categories: {
            necessary: {
                enabled: true,
                readOnly: true
            },
            analytics: {
                enabled: false
            }
        },
        language: {
            default: lang,  // Use the passed language (from localStorage)
            autoDetect: false,  // Disable auto-detect - we set it manually
            translations: translations
        },
        services: domainServices
    };
};

// Shared initialization function
const initializeCookieConsent = async (config) => {
    const runGlobalHandler = () => {
        if (window.onCookieConsentChange) {
            try {
                window.onCookieConsentChange();
            } catch (err) {
                console.error('onCookieConsentChange threw:', err);
            }
        }
    };

    // CookieConsent 3.x callback names
    config.onFirstConsent = runGlobalHandler; // First time consent is saved (Accept all / Save)
    config.onConsent = runGlobalHandler;      // Each time consent is saved thereafter
    config.onChange = runGlobalHandler;       // Each category toggle inside Preferences
    window.CookieConsent.run(config);
};

// Domain-agnostic consent change handler for all domains
window.onCookieConsentChange = () => {
    if (window.LOGGING && window.LOGGING.ENABLED) {
        console.log('[Cookies Debug] onCookieConsentChange called. Dispatching cookieConsentAccepted event.');
    }
    // Always dispatch event when consent is given or changed
    window.dispatchEvent(new CustomEvent('cookieConsentAccepted'));
    // Notify guide system
    import('../utils/guide.js').then(m => m.notifyGuideCookieConsentGiven && m.notifyGuideCookieConsentGiven());
    if (window.CookieConsent.acceptedCategory('analytics')) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'cookie_consent_granted' });
    }
}

// Helper: check if cookie consent has already been accepted or dismissed
export function isCookieConsentAccepted() {
    // Return true if the user has interacted with the cookie banner in any way (accepted, rejected, or closed)
    try {
        if (window.CookieConsent && window.CookieConsent.getCookie) {
            const consent = window.CookieConsent.getCookie();
            if (typeof window !== 'undefined' && window.LOGGING && window.LOGGING.ENABLED && window.LOGGING.CACHE) {
                console.log('[CookieConsent][Debug] Consent object:', consent);
            }
            // Accept if revision exists (accepted/rejected), or if dismissed/closed is set (any interaction)
            if (consent && (consent.revision || consent.dismissed || consent.closed || consent.status === 'dismissed' || consent.status === 'closed')) {
                return true;
            }
            // Some libraries set a boolean for dismissed/closed
            if (consent && (consent.dismissed === true || consent.closed === true)) {
                return true;
            }
            return false;
        }
        // Fallback: check localStorage for a known key/value that indicates explicit choice or dismissal
        const cc = localStorage.getItem('cc_cookie');
        if (cc && cc !== 'null' && cc !== '{}') {
            try {
                const parsed = JSON.parse(cc);
                if (parsed && (parsed.revision || parsed.dismissed || parsed.closed || parsed.status === 'dismissed' || parsed.status === 'closed')) return true;
                if (parsed && (parsed.dismissed === true || parsed.closed === true)) return true;
            } catch { }
        }
        return false;
    } catch (e) {
        return false;
    }
}

// Export shared functions for use in domain-specific files
// Also assign to window so they're available globally
window.CookieUtils = {
    getCookieConsentConfig,
    initializeCookieConsent,
    loadCookieTranslations
};

// Export function for initialization (will be called after language detection in index.js)
export const initializeAllCookies = async () => {
    if (window.LOGGING && window.LOGGING.ENABLED) {
        console.log('[Cookies] Initializing CookieConsent banner...');
    }

    // Get current language from localStorage (set by language detection in translations.js)
    const currentLang = localStorage.getItem('language') || 'en';
    if (window.LOGGING && window.LOGGING.ENABLED) {
        console.log(`[Cookies] Using language: ${currentLang}`);
    }

    const config = await getCookieConsentConfig({}, currentLang);
    await initializeCookieConsent(config);
    if (window.LOGGING && window.LOGGING.ENABLED) {
        console.log('[Cookies] CookieConsent banner initialized');
    }
};